// Nurse directory tab (Staff Roster redesign, 2026-07-27): search/filter
// table of all nurses. Extracted from StaffRoster.js's former inline "All
// Nurses" section. Owns its own search/duty filter state; receives the raw
// nurse list as a prop — no fetch of its own.
import React, { useMemo, useState } from "react";
import {
  Avatar, Box, Chip, InputAdornment, LinearProgress, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { NURSE_COLORS, initials, nurseAvatarColor } from "../theme";
import { useDebouncedValue } from "../hooks";

export default function NurseDirectoryTab({ nurses, loading }) {
  const [search, setSearch] = useState("");
  const [duty, setDuty] = useState("All");
  const debouncedSearch = useDebouncedValue(search);

  const rows = useMemo(() => {
    const term = debouncedSearch.toLowerCase().trim();
    return nurses
      .filter((n) => (duty === "All" ? true : duty === "On duty" ? n.on_duty : !n.on_duty))
      .filter((n) => !term || (n.name || "").toLowerCase().includes(term) || (n.nurse_id || "").toLowerCase().includes(term));
  }, [nurses, debouncedSearch, duty]);

  return (
    <Box>
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            size="small" placeholder="Search by name or ID" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            sx={{ flex: 1 }}
          />
          <TextField select size="small" label="Duty" value={duty} onChange={(e) => setDuty(e.target.value)} sx={{ minWidth: 160 }}>
            {["All", "On duty", "Off duty"].map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
          </TextField>
        </Stack>
      </Paper>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Nurse</TableCell><TableCell>ID</TableCell><TableCell>Specialization</TableCell><TableCell>Department</TableCell><TableCell>Default Hours</TableCell><TableCell>Duty</TableCell><TableCell align="right">Assigned Beds</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((n) => (
              <TableRow key={n.nurse_id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: nurseAvatarColor(n.nurse_id), width: 32, height: 32, fontSize: 14 }}>{initials(n.name)}</Avatar>
                    <Typography variant="body2">{n.name}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{n.nurse_id}</TableCell>
                <TableCell>{n.specialization || "—"}</TableCell>
                <TableCell>{n.department || "—"}</TableCell>
                <TableCell>{[n.shift_start, n.shift_end].filter(Boolean).join("–") || "—"}</TableCell>
                <TableCell><Chip size="small" label={n.on_duty ? "On duty" : "Off"} color={n.on_duty ? "success" : "default"} variant={n.on_duty ? "filled" : "outlined"} /></TableCell>
                <TableCell align="right"><Chip size="small" color="primary" label={n.assigned_beds ?? 0} /></TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No nurses match the filter.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
